import { useEffect, useState, useContext } from "react";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Input } from "components/input";
import { getQueryParams, useCurrentLocation } from "lib/utils";
import AuthContext from "contexts/AuthContext";
import { EmailValidation } from "../../components/email-validation";
import { isValidEmail } from "lib/utils";
import { useTranslation } from "react-i18next";
import InputValidation from "components/input-validation";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import ForgotPasswordLink from "components/forgot-password-link";
import PasswordInput from "components/password-input";
import stores from "consts/stores";
import FloodlightEvent from "components/floodlight-event";

export default function SignInPage() {
  const { t } = useTranslation();
  const [emailAlarm, setEmailAlarm] = useState(false);

  //reset right side visual
  const { setSidebarVisual, emailInput, setIsLoading } = useOutletContext();

  useEffect(() => {
    setSidebarVisual("man");
  }, [setSidebarVisual]);

  const { location: currentLocation } = useCurrentLocation();

  // Get query params
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = getQueryParams(location.search);
  const newQueryParams = {
    ...queryParams,
    newSignIn: "true",
  };

  const [username, setUsername] = useState(
    emailInput ||
      (queryParams.email && decodeURIComponent(queryParams.email)) ||
      ""
  );
  const [password, setPassword] = useState("");

  //handle Input change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setEmailAlarm(!isValidEmail(value));
  };

  const isKiosk = currentLocation?.takebackMethod
    ? stores.some(
        (store) =>
          currentLocation.takebackMethod.includes(store.value) &&
          store.return_type === "kiosk"
      )
    : false;

  //auth
  const { loginHandler, loginError, user } = useContext(AuthContext);

  const targetPath = queryParams.signUpOnly
    ? t("urls.my_trade_ins")
    : queryParams.w
      ? queryParams[TAKEBACK_METHOD_NAME] === "mail"
        ? t("urls.trade_in_terms")
        : isKiosk
          ? t("urls.trade_in_appointment")
          : t("urls.trade_in_summary")
      : t("urls.trade_in_summary");

  //handle submit
  const submitHandler = () => {
    setIsLoading(t("ui.signing_in"));
    loginHandler(
      { username, password },
      {
        onSuccess: () => {
          const navigateOptions = {
            //if only sign-in without case, navigate home. Otherwise continue with case
            pathname: targetPath,
          };
          if (newQueryParams) {
            navigateOptions.search =
              createSearchParams(newQueryParams).toString();
          }
          navigate(navigateOptions);
          navigate(0);
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        },
      },
      {
        onError: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const returnTo = `${location.pathname}${location.search}`;

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      onSubmit={submitHandler}
      submitParams={newQueryParams}
      secondaryLabel={t("ui.button_back")}
      secondaryHref={`${t("urls.trade_in_email")}`}
      secondaryParams={queryParams}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_signi+per_session" />
      <ProgressBar
        step={
          currentLocation?.state === null
            ? 4
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? 5
              : 4
        }
        showLabel
        stepsNum={
          currentLocation?.state === null
            ? user
              ? 5
              : 6
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? user
                ? 6
                : 7
              : isKiosk
                ? user
                  ? 5
                  : 6
                : user
                  ? 4
                  : 5
        }
      />
      <h2 className="font-semibold text-6xl max-w-lg">
        {t("ui.welcome_back")}
      </h2>
      <div className="w-full flex flex-col gap-x-large">
        <div>
          <Input
            label={t("ui.input_email")}
            type="email"
            id="username"
            value={username}
            onChange={handleEmailChange}
          />
          <EmailValidation emailAlarm={emailAlarm} />
        </div>
        <div>
          <PasswordInput
            label={t("ui.input_password")}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="text-blue mt-regular text-sm cursor-pointer">
            <ForgotPasswordLink returnToPath={returnTo} />
          </div>
        </div>
        <InputValidation errorMessage={loginError?.message} />
      </div>
    </FormContainer>
  );
}
